import { SaveData, Difficulty } from '../types';

const SAVE_KEY = 'zombie_lab_save';

export class SaveManager {
  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private getDefaultData(): SaveData {
    const progress: Record<string, { levelsCleared: number; unlocked: boolean }> = {};
    for (let i = 1; i <= 10; i++) {
      progress[`stage${i}`] = { levelsCleared: 0, unlocked: i === 1 };
    }

    return {
      playerData: {
        coins: 0,
        weapons: {
          pistol: { owned: true, level: 1 },
          shotgun: { owned: false, level: 0 },
          smg: { owned: false, level: 0 },
          rocketLauncher: { owned: false, level: 0 },
          assaultRifle: { owned: false, level: 0 },
          grenade: { owned: false, level: 0 },
        },
        progress,
      },
      settings: {
        language: 'ja',
        difficulty: Difficulty.NORMAL,
        soundVolume: { bgm: 0.7, se: 0.8 },
      },
    };
  }

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  save(): void {
    // Debounce: batch multiple saves into one write (avoids blocking main thread)
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      try {
        localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
      } catch (e) {
        console.error('Save failed:', e);
      }
    }, 50);
  }

  /** Force immediate save (for critical moments like closing tab) */
  saveNow(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch (e) {
      console.error('Save failed:', e);
    }
  }

  load(): SaveData {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed as SaveData;
      }
    } catch (e) {
      console.error('Load failed:', e);
    }
    return this.getDefaultData();
  }

  getData(): SaveData {
    return this.data;
  }

  getCoins(): number {
    return this.data.playerData.coins;
  }

  addCoins(amount: number): void {
    this.data.playerData.coins += Math.round(amount);
    this.save();
  }

  spendCoins(amount: number): boolean {
    if (this.data.playerData.coins < amount) return false;
    this.data.playerData.coins -= amount;
    this.save();
    return true;
  }

  isWeaponOwned(id: string): boolean {
    return this.data.playerData.weapons[id]?.owned ?? false;
  }

  getWeaponLevel(id: string): number {
    return this.data.playerData.weapons[id]?.level ?? 0;
  }

  purchaseWeapon(id: string, price: number): boolean {
    if (!this.spendCoins(price)) return false;
    this.data.playerData.weapons[id] = { owned: true, level: 1 };
    this.save();
    return true;
  }

  upgradeWeapon(id: string, cost: number): boolean {
    const weapon = this.data.playerData.weapons[id];
    if (!weapon?.owned || weapon.level >= 10) return false;
    if (!this.spendCoins(cost)) return false;
    weapon.level++;
    this.save();
    return true;
  }

  getLevelsCleared(stage: number): number {
    return this.data.playerData.progress[`stage${stage}`]?.levelsCleared ?? 0;
  }

  isStageUnlocked(stage: number): boolean {
    return this.data.playerData.progress[`stage${stage}`]?.unlocked ?? false;
  }

  completeLevel(stage: number, level: number): void {
    const key = `stage${stage}`;
    const progress = this.data.playerData.progress[key];
    if (progress && level > progress.levelsCleared) {
      progress.levelsCleared = level;
    }
    // Unlock next stage if level 10 cleared
    if (level >= 10) {
      const nextKey = `stage${stage + 1}`;
      if (this.data.playerData.progress[nextKey]) {
        this.data.playerData.progress[nextKey].unlocked = true;
      }
    }
    this.save();
  }

  getSettings() {
    return this.data.settings;
  }

  setSetting<K extends keyof SaveData['settings']>(key: K, value: SaveData['settings'][K]): void {
    this.data.settings[key] = value;
    this.save();
  }

  resetData(): void {
    this.data = this.getDefaultData();
    this.save();
  }
}
